import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { InvoiceWizardQuickSelectionProps } from "models/propTypes/invoicePropTypes";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { HTMLConstants, IconDefinitionConstants } from "utils/constants";
import { NestedKeyOf } from "models/utilTypes/nestedKeyType";
import { InvoiceFormTypes } from "models/formTypes";
//#endregion
//#region components
import { Grid } from "@mui/material";
import { InlineLabelDatePicker } from "components/common";
import { SelectInput } from "components/common/wintForms";
import { Portal } from "@mui/material";
//#endregion
import { DateHelper, EnumHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const InvoiceWizardQuickSelection: React.FC<InvoiceWizardQuickSelectionProps> = (props) => {
  const { isRecurring, invoice, connect, values } = props;
  const hideDeliveryDate = useSelector((store: AppState) => store.auth.selectedCompany.hideInvoiceDeliveryDate);

  const quickSelectionMount = document.getElementById(HTMLConstants.WizardQuickSelectionPortalId);

  const isRecurringStartDateEnabled =
    isRecurring &&
    (invoice?.id === undefined || invoice?.status === WintSuperkollAPIModel.RecurringInvoiceState.Created);

  const recurringStartDateProps = connect("recurringSettings.recurringInvoiceStartDate");
  const invoiceDateProps = connect("otherSettings.invoiceDate");
  const deliveryDateProps = connect("otherSettings.deliveryDate");

  /* This is an effect here since that means we don't need to fiddle with the callback here or down in the form.
   * This component is always mounted for the form, so the code is always going to be running.
   */
  React.useEffect(() => {
    const paymentTerms = values.otherSettings?.paymentTerms;
    const invoiceDate = invoiceDateProps.value;
    if (paymentTerms === undefined || invoiceDate === undefined || isNaN(Date.parse(invoiceDate.toString()))) return;

    const dueDate = DateHelper.addDays(invoiceDate, paymentTerms);
    if (DateHelper.isEqual(values.otherSettings?.dueDate, dueDate)) return;
    const dueDateFieldName: NestedKeyOf<InvoiceFormTypes.Create> = "otherSettings.dueDate";
    connect(dueDateFieldName).setValue(dueDate, true);
  }, [invoiceDateProps.value, values.otherSettings?.paymentTerms]);

  return (
    <Portal container={quickSelectionMount}>
      <Grid container item direction="row">
        {props.isRecurring ? (
          <>
            <InlineLabelDatePicker
              {...recurringStartDateProps}
              onChange={(date?: Date) => recurringStartDateProps.setValue(date, true)}
              label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.FirstInvoice)}
              disabled={!isRecurringStartDateEnabled}
            />
            <SelectInput
              {...connect("recurringSettings.recurringInvoiceFrequency")}
              icon={IconDefinitionConstants.WMonthCalendar}
              label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Interval)}
              items={EnumHelper.getNumericItemValues(WintSuperkollAPIModel.RecurringInvoiceFrequency).map((key) => {
                return {
                  value: Number(key),
                  label: LocalizationHelperInstance.getFormattedMessage(`Invoice.RecurringFrequency_${key}`),
                };
              })}
              className={cssExports.wizardHeaderField}
              rootClass={cssExports.wizardHeaderAutoComplete}
              disabled={!isRecurringStartDateEnabled}
            />
          </>
        ) : (
          <>
            <InlineLabelDatePicker
              {...invoiceDateProps}
              onChange={(date?: Date) => invoiceDateProps.setValue(date, true)}
              label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.InvoiceDate)}
            />
            {!hideDeliveryDate && (
              <InlineLabelDatePicker
                {...deliveryDateProps}
                onChange={(date?: Date) => deliveryDateProps.setValue(date, true)}
                label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.DeliveryDate)}
              />
            )}
          </>
        )}
      </Grid>
    </Portal>
  );
};

export default InvoiceWizardQuickSelection;
